import {
  AysClinicByDistanceResult,
  AysClinicSnSearchItem,
  AysClinicSearchResult,
  AysClinicContainer,
  AysClinicIdentifier,
  AysClinicSearchResponseItem,
  AysGeoLocation,
  BdAddress,
  ClinicSearchResponseDto,
  isClinicSearchResponseDto,
} from '@bindoc-projects/clinic-domain-lib';

/**
 * Folds the coordinates clinics-api carries NEXT TO the address onto the
 * address itself, as the `[lng, lat]` pair legacy consumers read.
 *
 * `GET /clinics/search` and `GET /clinics/{sn}/metadata` both answer with an
 * address that has no coordinates and a sibling `location: {lat, lng}` — while
 * every consumer of the legacy {@link AysClinicSearchResponseItem} / `AysClinic`
 * shape reads `address.location[0]` (lng) / `[1]` (lat). Dropping the sibling
 * therefore hands out an address whose `location` is `undefined`, and those
 * consumers throw (the cube map tile's "Adresse von Klinik" centre did, which
 * silently left the tile on its previous centre — BDA-5804 follow-up).
 *
 * Longitude FIRST: a swap fails silently, putting Charité in the Indian Ocean
 * rather than raising. An address that already carries coordinates wins — the
 * `by-condition` / `by-distance` endpoints deliver them inside `address` and
 * must not be second-guessed.
 */
export const AYS_CLINIC_ADDRESS_WITH_LOCATION = (
  address: BdAddress | null | undefined,
  geo: AysGeoLocation | null | undefined,
): BdAddress | null => {
  if (!address || geo == null || address.location != null) {
    return address ?? null;
  }

  return new BdAddress(
    address.street,
    address.postalCode,
    address.city,
    [geo.lng, geo.lat],
    address.note,
    address.administrativeDistrictId,
  );
};

export const AYS_CLINIC_SEARCH_RESPONSE_FROM_OBJ_FN = (
  obj: unknown
): AysClinicSearchResponseItem => {
  if (!isClinicSearchResponseDto(obj)) {
    throw new TypeError(
      'Invalid clinic search response object: missing required properties'
    );
  }

  const typedObj: ClinicSearchResponseDto = obj;
  const address = typedObj.address
    ? BdAddress.fromObject(typedObj.address)
    : new BdAddress();
  // clinics-api flattened `id: {ik, sn, year}` into the clinicSn/clinicIk/
  // internalSn/year quartet; `fromClinicNode` maps it back to the legacy identity.
  const id = AysClinicIdentifier.fromClinicNode(typedObj);

  return {
    id: id,
    address: address,
    name: typedObj.name,
  };
};

export const AYS_CLINIC_SEARCH_RESULT_TO_ITEM = (
  result: AysClinicSearchResult,
): AysClinicSearchResponseItem => ({
  id: new AysClinicIdentifier(result.clinicIk, result.clinicSn),
  address:
    AYS_CLINIC_ADDRESS_WITH_LOCATION(result.address, result.location) ??
    new BdAddress(),
  name: result.name,
});

/**
 * SN-native `/clinics/by-distance/` result → the legacy container shape.
 *
 * Lives here with the other SN→legacy mappers rather than inline in the feature
 * that needs it, so a second caller does not hand-roll a subtly different
 * version. It is what `ClinicSnReadAdapter.searchByDistance` used to do before
 * that method was removed from the contract (BDA-5809 step 2/3); scenario-analysis
 * is the remaining caller, still typed on the legacy container. Two details it
 * pins down:
 *
 * - the id convention is `(clinicIk, clinicSn)`, matching every other SN→legacy
 *   mapper here, so ids stay comparable across search / by-distance / getAll;
 * - geoIndex carries the coordinates on `address.location` ([lng, lat]) rather
 *   than in a dedicated geometry point, so they are surfaced as the container
 *   point — consumers read `geometry.point` and would otherwise see nothing.
 */
/**
 * SN-typed contract row → the legacy row a UI layer still exposes.
 *
 * BDA-5809 step 3 re-typed `ClinicReadService` onto the opaque clinicSn, but the
 * clinic pickers keep a legacy-typed public surface so their ~10 consumers did not
 * all have to move in the same PR. This is the single place that mapping happens.
 *
 * Lossless: it reproduces the convention the SN mappers have used since the DE
 * flip — the opaque clinicSn in the `sn` slot, the display `ik` in `ik` — so the
 * ids emitted to consumers are byte-identical to the pre-step-3 ones.
 */
export const AYS_CLINIC_SN_ITEM_TO_ITEM = (
  item: AysClinicSnSearchItem,
): AysClinicSearchResponseItem => ({
  id: AysClinicIdentifier.fromSnRef(item.id),
  name: item.name,
  address: item.address ?? new BdAddress(),
  beds: item.beds,
});

export const AYS_CLINIC_BY_DISTANCE_TO_CONTAINER = (
  result: AysClinicByDistanceResult,
): AysClinicContainer => ({
  clinic: {
    id: new AysClinicIdentifier(result.clinicIk, result.clinicSn),
    name: result.name ?? null,
    address: result.address,
  },
  geometry: {
    distance: result.distanceMeters ?? undefined,
    point: result.address?.location,
  },
});
